import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ProductCard } from './ProductCard';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';

// Detect if we're on Emergent preview (use backend) or elsewhere like Netlify (use local /api)
const isEmergentPreview = typeof window !== 'undefined' && window.location.hostname.includes('emergentagent.com');
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Only use backend URL on Emergent preview, otherwise use relative /api path for Netlify Functions
const API = (isEmergentPreview && BACKEND_URL) ? `${BACKEND_URL}/api` : '/api';

export const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data.products || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Unable to load products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <section
      className="py-16 md:py-24 bg-wild-sand"
      data-testid="featured-products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2
            className="font-headings text-4xl md:text-5xl tracking-wider text-wild-dark mb-4"
            data-testid="featured-products-title"
          >
            FEATURED PRODUCTS
          </h2>
          <p className="font-subheadings text-lg md:text-xl text-wild-stone italic max-w-2xl mx-auto">
            Our most popular designs—crafted for comfort, built for the wild.
          </p>
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="h-4 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div
            className="text-center py-12"
            data-testid="featured-products-error"
          >
            <p className="font-body text-wild-stone mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-wild-leather text-white hover:bg-wild-leather/90"
            >
              Try Again
            </Button>
          </div>
        ) : products.length === 0 ? (
          <div
            className="text-center py-12"
            data-testid="featured-products-empty"
          >
            <p className="font-body text-wild-stone">
              No products available yet. Check back soon!
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12"
            data-testid="featured-products-grid"
          >
            {products.slice(0, 4).map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                featured={index < 2}
              />
            ))}
          </div>
        )}

        {/* View All Button */}
        {!isLoading && !error && products.length > 0 && (
          <div className="text-center mt-12">
            <Link to="/shop">
              <Button
                variant="outline"
                className="border-2 border-wild-leather text-wild-leather hover:bg-wild-leather hover:text-white px-10 py-6 text-lg font-headings tracking-widest"
                data-testid="view-all-products-btn"
              >
                VIEW ALL PRODUCTS
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
